import { useTranslation } from "react-i18next";
import { getLocale, translate as tr } from "../../i18n";
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import WorkflowQueueCard from '../common/WorkflowQueueCard';
import '../../assets/css/attendance.css';

function formatDate(isoString) {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleDateString(getLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(isoString) {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

function localInputToISO(val) {
  if (!val) return null;
  return new Date(val).toISOString();
}

function isoToLocalInput(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatLocalDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function breakHint(checkInValue, checkOutValue) {
  if (!checkInValue || !checkOutValue) return null;
  const checkIn = new Date(checkInValue);
  const checkOut = new Date(checkOutValue);
  if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime()) || checkOut <= checkIn) return null;

  const workedSeconds = Math.floor((checkOut - checkIn) / 1000);
  if (workedSeconds <= 6 * 3600) {
    return {
      stillViolation: false,
      message: tr("This edit brings the shift down to 6 hours or less, so the break violation should clear after saving."),
    };
  }

  const safeCheckout = new Date(checkIn.getTime() + 6 * 3600 * 1000);
  return {
    stillViolation: true,
    message: `This edit still records more than 6 hours with no break. To clear the violation here, set check-out to ${formatLocalDateTime(safeCheckout)} or earlier, or add a break record separately.`,
  };
}

function actionLabel(entry) {
  return entry?.break_violation ? tr("Review") : tr("Fix");
}

function modalTitle(entry) {
  return entry?.break_violation ? tr("Review Break Violation") : tr("Fix Attendance Entry");
}

function saveLabel(entry, hint) {
  if (!entry?.break_violation) return tr("Save");
  if (hint?.stillViolation === false) return tr("Save and Resolve");
  return tr("Save Review");
}

const STATUS_LABELS = {
  missing_checkout: { label: 'Missing checkout', color: '#ef4444', bg: '#fef2f2', border: '#fca5a5' },
  absent          : { label: 'Absent',           color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
  present         : { label: 'Present',           color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
  late            : { label: 'Late',              color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
};

function StatusBadge({ status, breakViolation = false }) {
  const s = breakViolation
    ? { label: 'Break violation', color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' }
    : (STATUS_LABELS[status] || STATUS_LABELS.absent);
  return (
    <span className="att-badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
      {tr(s.label)}
    </span>
  );
}

export default function AttendancePage() {
  useTranslation();
  const auth         = useSelector((s) => s.auth);
  const isPrivileged = auth.is_workspace_admin === 1 || ['ADMIN', 'MANAGER'].includes(auth.workspace_role);

  const [tab, setTab]             = useState('issues');
  const [issues, setIssues]       = useState([]);
  const [overtime, setOvertime]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [otLoading, setOtLoading] = useState(false);

  const [editOpen, setEditOpen]         = useState(false);
  const [selected, setSelected]         = useState(null);
  const [editCheckIn, setEditCheckIn]   = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editNote, setEditNote]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [requestingCorrection, setRequestingCorrection] = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const activeBreakHint = selected?.break_violation ? breakHint(editCheckIn, editCheckOut) : null;

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
  });

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/api/attendance/issues?workspace_id=${auth.workspace_id}`,
        authHeaders()
      );
      setIssues(res.data.results || []);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to load issues"));
    } finally {
      setLoading(false);
    }
  };

  const fetchOvertime = async () => {
    setOtLoading(true);
    try {
      const res = await axios.get(
        `/api/attendance/overtime?workspace_id=${auth.workspace_id}`,
        authHeaders()
      );
      setOvertime(res.data.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setOtLoading(false);
    }
  };

  useEffect(() => { fetchIssues(); }, [auth.workspace_id]);

  useEffect(() => {
    if (tab === 'overtime' && overtime.length === 0) fetchOvertime();
  }, [tab]);

  const openEdit = (entry) => {
    setSelected(entry);
    setEditCheckIn(isoToLocalInput(entry.check_in));
    setEditCheckOut(isoToLocalInput(entry.check_out));
    setEditNote(entry.note || '');
    setError('');
    setSuccess('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    if (!editCheckIn) { setError(tr("Check-in time is required")); return; }

    setSaving(true);
    setError('');
    try {
      await axios.put(
        `/api/attendance/${selected.id}`,
        JSON.stringify({
          workspace_id: auth.workspace_id,
          check_in    : localInputToISO(editCheckIn),
          check_out   : editCheckOut ? localInputToISO(editCheckOut) : null,
          note        : editNote,
        }),
        authHeaders()
      );
      setEditOpen(false);
      setSuccess(tr("Entry updated successfully"));
      setTimeout(() => setSuccess(''), 3000);
      fetchIssues();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const acknowledgeBreak = async () => {
    if (!selected) return;
    setAcknowledging(true);
    setError('');
    try {
      await axios.post(
        `/api/attendance/${selected.id}/acknowledge-break`,
        JSON.stringify({ workspace_id: auth.workspace_id }),
        authHeaders()
      );
      setEditOpen(false);
      setSuccess(tr("Break violation acknowledged."));
      setTimeout(() => setSuccess(''), 3000);
      fetchIssues();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to acknowledge"));
    } finally {
      setAcknowledging(false);
    }
  };

  const requestCorrection = async () => {
    if (!selected) return;
    setRequestingCorrection(true);
    setError('');
    try {
      await axios.post(
        `/api/attendance/${selected.id}/request-correction`,
        JSON.stringify({ workspace_id: auth.workspace_id }),
        authHeaders()
      );
      setSuccess(tr("Correction request sent to employee."));
      setTimeout(() => setSuccess(''), 3000);
      fetchIssues();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || tr("Failed to request correction"));
    } finally {
      setRequestingCorrection(false);
    }
  };

  if (!isPrivileged) {
    return (
      <div>
        <Sidebar />
        <div className="att-page">
          <div className="att-header">
            <h2 className="att-title">{tr("Attendance")}</h2>
          </div>
          <p style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280' }}>{tr("You don't have permission to view Attendance records.")} </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Sidebar />
      <div className="att-page">
        <div className="att-header">
          <h2 className="att-title">{tr("Attendance")}</h2>
          <p className="att-subtitle">{tr("Review issues and overtime across your workspace.")}</p>
        </div>

        <div style={{ marginBottom: 18 }}>
          <WorkflowQueueCard
            workspaceId={auth.workspace_id}
            token={auth.token}
            title={tr("Open Workflow Queue")}
            actionLabel="Open Leave"
            actionUrl="/leave"
          />
        </div>

        {success && <div className="att-toast att-toast--success">{success}</div>}

        {/* Tabs */}
        <div className="att-tabs">
          <button className={`att-tab${tab === 'issues' ? ' att-tab--active' : ''}`} onClick={() => setTab('issues')}>{tr("Issues")} {issues.length > 0 && <span className="att-tab-badge">{issues.length}</span>}
          </button>
          <button className={`att-tab${tab === 'overtime' ? ' att-tab--active' : ''}`} onClick={() => setTab('overtime')}>{tr("Overtime")} </button>
        </div>

        {/* ── Issues tab ── */}
        {tab === 'issues' && (
          loading ? (
            <div className="att-empty">{tr("Loading...")}</div>
          ) : issues.length === 0 ? (
            <div className="att-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p>{tr("No issues found. All attendance entries look good.")}</p>
            </div>
          ) : (
            <div className="att-table-wrap">
              <div className="att-row att-row--header">
                <div className="att-cell att-cell--name">{tr("Employee")}</div>
                <div className="att-cell att-cell--date">{tr("Date")}</div>
                <div className="att-cell att-cell--status">{tr("Status")}</div>
                <div className="att-cell att-cell--checkin">{tr("Check In")}</div>
                <div className="att-cell att-cell--checkout">{tr("Check Out")}</div>
                <div className="att-cell att-cell--worked">{tr("Worked")}</div>
                <div className="att-cell att-cell--actions" />
              </div>
              {issues.map((entry) => (
                <div key={entry.id} className="att-row att-row--data">
                  <div className="att-cell att-cell--name">
                    <div className="att-name">{entry.user?.first_name} {entry.user?.last_name}</div>
                    <div className="att-email">{entry.user?.email}</div>
                  </div>
                  <div className="att-cell att-cell--date">{formatDate(entry.date)}</div>
                  <div className="att-cell att-cell--status"><StatusBadge status={entry.status} breakViolation={entry.break_violation} /></div>
                  <div className="att-cell att-cell--checkin">{formatTime(entry.check_in)}</div>
                  <div className="att-cell att-cell--checkout">{formatTime(entry.check_out)}</div>
                  <div className="att-cell att-cell--worked">{formatDuration(entry.worked_seconds)}</div>
                  <div className="att-cell att-cell--actions">
                    <button className="att-fix-btn" onClick={() => openEdit(entry)}>{actionLabel(entry)}</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── Overtime tab ── */}
        {tab === 'overtime' && (
          otLoading ? (
            <div className="att-empty">{tr("Loading...")}</div>
          ) : overtime.length === 0 ? (
            <div className="att-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <p>{tr("No overtime entries in the last 30 days. Make sure overtime is enabled in Settings.")}</p>
            </div>
          ) : (
            <div className="att-table-wrap">
              <div className="att-row att-row--header att-row--ot">
                <div className="att-cell att-cell--name">{tr("Employee")}</div>
                <div className="att-cell att-cell--date">{tr("Date")}</div>
                <div className="att-cell att-cell--checkin">{tr("Check In")}</div>
                <div className="att-cell att-cell--checkout">{tr("Check Out")}</div>
                <div className="att-cell att-cell--worked">{tr("Worked")}</div>
                <div className="att-cell att-cell--ot">{tr("Overtime")}</div>
              </div>
              {overtime.map((entry) => (
                <div key={entry.id} className="att-row att-row--data att-row--ot">
                  <div className="att-cell att-cell--name">
                    <div className="att-name">{entry.user?.first_name} {entry.user?.last_name}</div>
                    <div className="att-email">{entry.user?.email}</div>
                  </div>
                  <div className="att-cell att-cell--date">{formatDate(entry.date)}</div>
                  <div className="att-cell att-cell--checkin">{formatTime(entry.check_in)}</div>
                  <div className="att-cell att-cell--checkout">{formatTime(entry.check_out)}</div>
                  <div className="att-cell att-cell--worked">{formatDuration(entry.worked_seconds)}</div>
                  <div className="att-cell att-cell--ot">
                    {entry.overtime_seconds > 0 && (
                      <span className="att-ot-badge">{formatDuration(entry.overtime_seconds)}</span>
                    )}
                    {entry.legal_ot_violation && (
                      <span className="att-ot-badge att-ot-badge--legal" title={tr("ArbZG §3: worked over 10h daily maximum")}>{tr("Legal max")}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <Footer />

      {/* ── Fix Modal ── */}
      <Modal show={editOpen} onHide={() => setEditOpen(false)} dialogClassName="add-New-tag-popup-box-container" bsPrefix="">
        <div className="pm-header" style={{ background: '#1a5c30' }}>
          <h3 className="pm-title">{modalTitle(selected)}</h3>
          <button className="pm-close" onClick={() => setEditOpen(false)}>x</button>
        </div>

        {selected && (
          <div className="pm-body">
            <div className="att-modal-meta">
              <strong>{selected.user?.first_name} {selected.user?.last_name}</strong>
              <span>{formatDate(selected.date)}</span>
              <StatusBadge status={selected.status} breakViolation={selected.break_violation} />
            </div>

            {selected.break_violation && (
              <div className="att-modal-alert">{tr("This entry was flagged because the recorded break time is below the configured compliance threshold.")} </div>
            )}

            {activeBreakHint && (
              <div className={`att-modal-hint${activeBreakHint.stillViolation ? ' att-modal-hint--warning' : ''}`}>
                {activeBreakHint.message}
              </div>
            )}

            {selected.break_violation && (
              <div className="att-modal-note">{tr("Use this screen to review the flagged record. If the times stay non-compliant, the violation remains visible for follow-up.")} </div>
            )}

            {selected.breaks?.length > 0 && (
              <div className="att-modal-breaks">
                <span className="att-modal-breaks-label">{tr("Breaks recorded:")}</span>
                {selected.breaks.map((b, i) => (
                  <span key={i} className="att-modal-break-item">
                    {formatTime(b.break_start)} - {formatTime(b.break_end)}
                  </span>
                ))}
              </div>
            )}

            <div className="pm-field">
              <label className="pm-label">{tr("Check In")} <span style={{ color: '#dc2626' }}>*</span></label>
              <input className="pm-input" type="datetime-local" value={editCheckIn} onChange={(e) => setEditCheckIn(e.target.value)} />
            </div>

            <div className="pm-field">
              <label className="pm-label">{tr("Check Out")}</label>
              <input className="pm-input" type="datetime-local" value={editCheckOut} onChange={(e) => setEditCheckOut(e.target.value)} />
              <span className="pm-label-hint">{tr("Leave blank to keep as missing checkout.")}</span>
            </div>

            <div className="pm-field">
              <label className="pm-label">{tr("Note")}</label>
              <input className="pm-input" type="text" placeholder={tr("Reason for manual edit...")} value={editNote} onChange={(e) => setEditNote(e.target.value)} />
            </div>

            {error && <div className="att-modal-error">{error}</div>}
          </div>
        )}

        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => setEditOpen(false)}>{tr("Cancel")}</button>
          {selected?.break_violation && (
            <>
              <button className="att-secondary-btn" onClick={requestCorrection} disabled={requestingCorrection || acknowledging || saving}>
                {requestingCorrection ? tr("Sending...") : tr("Request Correction")}
              </button>
              <button className="att-secondary-btn att-secondary-btn--muted" onClick={acknowledgeBreak} disabled={acknowledging || requestingCorrection || saving}>
                {acknowledging ? tr("Saving...") : tr("Acknowledge")}
              </button>
            </>
          )}
          <button className="pm-btn-create" onClick={saveEdit} disabled={saving}>
            {saving ? tr("Saving...") : saveLabel(selected, activeBreakHint)}
          </button>
        </div>
      </Modal>
    </div>
  );
}
